import { __extends } from "tslib";
import * as React from 'react';
import { PureComponent } from 'react';
import * as ReactDOM from 'react-dom';
import cn from 'classnames';
import 'raf';
import Portal from '@mos-cat/portal';
import PopoverWrapperContent from './inner/PopoverWrapperContent';
import { APPEARANCE_DURATION, CHECK_CHILD_INTERVAL, FULL_HEADER_HEIGHT_CHANGE_EVENT, HEADER_INIT_EVENT, POSITION, POSITIONS } from './inner/constants';
import { getElementDocumentOffset, getHeaderRealHeight, getPopoverPointerStyle, getPopoverPosition, getPopoverStyle } from './inner/helpers';
import './styles/PopoverWrapper.scss';
var PopoverWrapper = /** @class */ (function (_super) {
    __extends(PopoverWrapper, _super);
    function PopoverWrapper() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.animationFrameId = null;
        _this.checkCoordinatesIntervalId = null;
        _this.childRef = React.createRef();
        _this.portalRef = React.createRef();
        _this.pointerRef = React.createRef();
        _this.state = {
            headerHeight: 0,
            childRect: { top: null, right: null, bottom: null, left: null },
            pointerClassName: 'PopoverWrapper__pointer'
        };
        _this.setChildCheckInterval = function () {
            _this.checkCoordinatesIntervalId = window.setInterval(_this.setChildCoordinates, CHECK_CHILD_INTERVAL);
        };
        _this.setChildCoordinates = function () {
            var childRect = _this.state.childRect;
            var childRef = _this.childRef;
            /* eslint-disable-next-line react/no-find-dom-node */
            var childElement = ReactDOM.findDOMNode(childRef.current);
            var newChildRect = getElementDocumentOffset(childElement);
            var rectChanged = newChildRect.top !== childRect.top ||
                newChildRect.right !== childRect.right ||
                newChildRect.bottom !== childRect.bottom ||
                newChildRect.left !== childRect.left;
            if (rectChanged) {
                _this.setState({ childRect: newChildRect });
            }
        };
        _this.adjustPopoverSize = function (childElement) {
            var adjustPopoverSize = _this.props.adjustPopoverSize;
            if (!adjustPopoverSize || !_this.validateRefs())
                return;
            var portalElement = _this.portalRef.current.element;
            var width = childElement.clientWidth;
            var _a = window.getComputedStyle(childElement), borderLeftWidth = _a.borderLeftWidth, borderRightWidth = _a.borderRightWidth;
            portalElement.style.width = width +
                parseFloat(borderLeftWidth) +
                parseFloat(borderRightWidth) + "px";
        };
        _this.validateRefs = function () {
            var _a = _this, portalRef = _a.portalRef, pointerRef = _a.pointerRef, childRef = _a.childRef;
            return portalRef.current && pointerRef.current && childRef.current;
        };
        _this.invalidate = function () {
            var _a = _this.props, modal = _a.modal, position = _a.position, floating = _a.floating;
            var headerHeight = _this.state.headerHeight;
            var _b = _this, childRef = _b.childRef, portalRef = _b.portalRef;
            if (!_this.validateRefs()) {
                return;
            }
            var portalElement = portalRef.current.element;
            /* eslint-disable-next-line react/no-find-dom-node */
            var childElement = ReactDOM.findDOMNode(childRef.current);
            window.cancelAnimationFrame(_this.animationFrameId);
            _this.animationFrameId = window.requestAnimationFrame(function () {
                var calculatedPosition = getPopoverPosition(position, modal, floating, headerHeight, portalElement, childElement);
                _this.adjustPopoverSize(childElement);
                _this.setPopoverStyle();
                _this.setPopoverClassName(calculatedPosition);
                _this.setPopoverPosition(calculatedPosition, childElement);
                _this.setPointerClassName(calculatedPosition);
                _this.setPointerPosition(calculatedPosition, childElement);
            });
        };
        _this.subscribeHeaderHeightChange = function () {
            window.$mos_layouts.on(FULL_HEADER_HEIGHT_CHANGE_EVENT, _this.handleHeaderHeightChange);
        };
        _this.handleHeaderHeightChange = function () {
            _this.setState({ headerHeight: getHeaderRealHeight() });
        };
        return _this;
    }
    PopoverWrapper.prototype.componentDidMount = function () {
        var _a = this.props, open = _a.open, trackChildPosition = _a.trackChildPosition, considerHeader = _a.considerHeader;
        // Контейнер поповера
        var portalElement = this.portalRef.current.element;
        portalElement.className = 'PopoverWrapper__portal';
        this.invalidate();
        window.addEventListener('resize', this.invalidate);
        // Подписка на события хэдера
        if (considerHeader) {
            if (window.$mos_layouts && window.$mos_layouts.on) {
                // Если хэдер уже проинициализирован
                this.subscribeHeaderHeightChange();
            }
            else {
                // Если хэдер ещё не был проинициализирован
                document.addEventListener(HEADER_INIT_EVENT, this.subscribeHeaderHeightChange, false);
            }
        }
        // Запуск проверки изменения положения только для открытого поповера
        if (trackChildPosition && open) {
            this.setChildCheckInterval();
        }
    };
    PopoverWrapper.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, open = _a.open, trackChildPosition = _a.trackChildPosition;
        var justOpened = !prevProps.open && open;
        var justClosed = prevProps.open && !open;
        var trackChildPositionEnabled = !prevProps.trackChildPosition && trackChildPosition;
        var trackChildPositionDisabled = prevProps.trackChildPosition && !trackChildPosition;
        var childCheckByOpenEnabled = trackChildPosition && justOpened;
        var childCheckByPropsEnabled = trackChildPositionEnabled && open;
        // Запуск проверки координат для открывшегося поповера или по свойству
        if (childCheckByOpenEnabled || childCheckByPropsEnabled) {
            this.setChildCheckInterval();
        }
        // Остановка проверки координат для закрывшегося поповера или по свойству
        if (justClosed || trackChildPositionDisabled) {
            window.clearInterval(this.checkCoordinatesIntervalId);
        }
        this.invalidate();
    };
    PopoverWrapper.prototype.componentWillUnmount = function () {
        var considerHeader = this.props.considerHeader;
        var _a = this, animationFrameId = _a.animationFrameId, checkCoordinatesIntervalId = _a.checkCoordinatesIntervalId;
        window.clearInterval(checkCoordinatesIntervalId);
        window.cancelAnimationFrame(animationFrameId);
        window.removeEventListener('resize', this.invalidate);
        // Отписка от событий хэдера
        if (considerHeader) {
            document.removeEventListener(HEADER_INIT_EVENT, this.subscribeHeaderHeightChange);
            if (window.$mos_layouts && window.$mos_layouts.off) {
                window.$mos_layouts.off(FULL_HEADER_HEIGHT_CHANGE_EVENT, this.handleHeaderHeightChange);
            }
        }
    };
    PopoverWrapper.prototype.setPopoverStyle = function () {
        if (!this.validateRefs()) {
            return;
        }
        var appearanceDuration = this.props.appearanceDuration;
        var portalElement = this.portalRef.current.element;
        portalElement.style.transition = "visibility " + appearanceDuration + "ms, opacity " + appearanceDuration + "ms";
    };
    PopoverWrapper.prototype.setPopoverClassName = function (position) {
        if (!this.validateRefs()) {
            return;
        }
        var _a = this.props, open = _a.open, modal = _a.modal, className = _a.className;
        var portalElement = this.portalRef.current.element;
        portalElement.className = cn('PopoverWrapper__portal', "PopoverWrapper__position_" + position, {
            PopoverWrapper__portal_visible: open,
            PopoverWrapper__portal_modal: modal
        }, className);
    };
    PopoverWrapper.prototype.setPopoverPosition = function (position, childElement) {
        if (!this.validateRefs()) {
            return;
        }
        var floating = this.props.floating;
        var portalElement = this.portalRef.current.element;
        var _a = getPopoverStyle(position, floating, portalElement, childElement), top = _a.top, right = _a.right, bottom = _a.bottom, left = _a.left, transform = _a.transform;
        portalElement.style.top = top;
        portalElement.style.right = right;
        portalElement.style.bottom = bottom;
        portalElement.style.left = left;
        portalElement.style.transform = transform;
    };
    PopoverWrapper.prototype.setPointerClassName = function (position) {
        this.setState({
            pointerClassName: cn('PopoverWrapper__pointer', "PopoverWrapper__pointer-" + position)
        });
    };
    PopoverWrapper.prototype.setPointerPosition = function (position, childElement) {
        if (!this.validateRefs()) {
            return;
        }
        var floating = this.props.floating;
        var _a = this, pointerRef = _a.pointerRef, portalRef = _a.portalRef;
        var portalElement = portalRef.current.element;
        var pointerElement = pointerRef.current;
        var _b = getPopoverPointerStyle(position, floating, portalElement, childElement, pointerElement), top = _b.top, right = _b.right, bottom = _b.bottom, left = _b.left;
        pointerElement.style.top = top;
        pointerElement.style.right = right;
        pointerElement.style.bottom = bottom;
        pointerElement.style.left = left;
    };
    PopoverWrapper.prototype.render = function () {
        var _a = this.props, children = _a.children, content = _a.content;
        var pointerClassName = this.state.pointerClassName;
        var _b = this, childRef = _b.childRef, portalRef = _b.portalRef, pointerRef = _b.pointerRef;
        var child = React.Children.only(children);
        return (React.createElement(React.Fragment, null,
            React.createElement(PopoverWrapperContent, { ref: childRef }, child),
            React.createElement(Portal, { refProp: portalRef },
                React.createElement("div", { ref: pointerRef, className: pointerClassName }),
                content)));
    };
    PopoverWrapper.defaultProps = {
        position: POSITION.top,
        open: false,
        modal: false,
        className: '',
        appearanceDuration: APPEARANCE_DURATION,
        floating: false,
        considerHeader: false,
        trackChildPosition: true,
        adjustPopoverSize: false
    };
    return PopoverWrapper;
}(PureComponent));
export { PopoverWrapper as default, POSITIONS, POSITION, APPEARANCE_DURATION };
//# sourceMappingURL=PopoverWrapper.js.map